
import React, { useState } from 'react';
import { SidebarProvider, Sidebar, SidebarContent, SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarGroup, SidebarGroupLabel, SidebarInset } from '@/components/ui/sidebar';
import { LayoutDashboard, FileText, BarChart2, DollarSign, Settings, User, Plus, Moon, Sun, PieChart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DashboardMetrics from '@/components/dashboard/DashboardMetrics';
import DashboardOverview from '@/components/dashboard/DashboardOverview';
import Editor from '@/components/dashboard/Editor';
import { useToast } from '@/hooks/use-toast';
import { Route, Routes, Link, useLocation, Navigate } from 'react-router-dom';
import ArticleSettings from '@/components/dashboard/ArticleSettings';
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from '@/components/ui/navigation-menu';
import Analytics from '@/components/dashboard/Analytics';
import DashboardContent from '@/components/dashboard/DashboardContent';
import { DashboardProvider, useDashboard } from '@/contexts/DashboardContext';
import GlobalHeader from '@/components/GlobalHeader';
import { useAuth } from '@/hooks/useAuth';

// Main Dashboard component that wraps everything with DashboardProvider
const Dashboard = () => {
  return <DashboardProvider>
      <DashboardWrapper />
    </DashboardProvider>;
};

// Inner component that uses the dashboard context
const DashboardWrapper = () => {
  const [darkMode, setDarkMode] = useState(false);
  const { toast } = useToast();
  const location = useLocation();
  const { stats } = useDashboard();
  const { userProfile, user } = useAuth();
  
  // Check if the current route is an editor route
  const isEditorRoute = location.pathname.includes('/editor/');
  
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
    toast({
      title: darkMode ? "Light mode enabled" : "Dark mode enabled",
      description: "Your preference has been saved."
    });
  };
  
  const userRole = userProfile?.user_role || user?.user_metadata?.user_role || 'journalist';
  
  return <div className={`min-h-screen ${darkMode ? 'dark' : ''}`}>
      <GlobalHeader notificationCount={3} subscriptionStatus="Premium" walletBalance={25.5} draftCount={2} userRole={userRole} />
      <div className="pt-16">
        <SidebarProvider defaultOpen={!isEditorRoute}>
          <div className="flex min-h-screen w-full">
            {!isEditorRoute && (
              <Sidebar>
                <SidebarContent>
                  <SidebarGroup>
                    <SidebarGroupLabel>Dashboard</SidebarGroupLabel>
                    <SidebarMenu>
                      <SidebarMenuItem>
                        <SidebarMenuButton asChild isActive={location.pathname === '/dashboard'}>
                          <Link to="/dashboard">
                            <LayoutDashboard />
                            <span>Overview</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem>
                        <SidebarMenuButton asChild isActive={location.pathname === '/dashboard/metrics'}>
                          <Link to="/dashboard/metrics">
                            <PieChart />
                            <span>Metrics</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem>
                        <SidebarMenuButton asChild isActive={location.pathname === '/dashboard/content'}>
                          <Link to="/dashboard/content">
                            <FileText />
                            <span>Content</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem>
                        <SidebarMenuButton asChild isActive={location.pathname === '/dashboard/analytics'}>
                          <Link to="/dashboard/analytics">
                            <BarChart2 />
                            <span>Analytics</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem>
                        <SidebarMenuButton asChild isActive={location.pathname === '/dashboard/earnings'}>
                          <Link to="/dashboard/earnings">
                            <DollarSign />
                            <span>Earnings</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem>
                        <SidebarMenuButton asChild isActive={location.pathname === '/dashboard/settings'}>
                          <Link to="/dashboard/settings">
                            <Settings />
                            <span>Settings</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem>
                        <SidebarMenuButton asChild isActive={location.pathname === '/dashboard/profile'}>
                          <Link to="/dashboard/profile">
                            <User />
                            <span>Profile</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </SidebarMenu>
                  </SidebarGroup>
                  <div className="mt-auto p-4">
                    <Button variant="outline" size="icon" onClick={toggleDarkMode} className="w-full flex items-center justify-center gap-2">
                      {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                      <span>{darkMode ? "Light Mode" : "Dark Mode"}</span>
                    </Button>
                  </div>
                </SidebarContent>
              </Sidebar>
            )}

            <SidebarInset className={isEditorRoute ? "p-0 w-full" : "p-6"}>
              <div className="w-full">
                {!isEditorRoute && (
                  <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold text-directpress-blue dark:text-white">Journalist Dashboard</h1>
                    <Button asChild className="bg-directpress-orange hover:bg-orange-600 text-white">
                    </Button>
                  </div>
                )}

                <Routes>
                  <Route path="/" element={<DashboardOverview stats={stats} />} />
                  <Route path="/metrics" element={<DashboardMetrics />} />
                  <Route path="/content" element={<DashboardContent />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/earnings" element={<DashboardEarnings />} />
                  <Route path="/settings" element={<DashboardSettings />} />
                  <Route path="/profile" element={<DashboardProfile />} />
                  <Route path="/editor/new" element={<Editor />} />
                  <Route path="/editor/:id" element={<Editor />} />
                </Routes>
              </div>
            </SidebarInset>
          </div>
        </SidebarProvider>
      </div>
    </div>;
};

// Placeholder components for each route
const DashboardEarnings = () => <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
    <h2 className="text-2xl font-bold mb-4">Earnings</h2>
    <p>Monitor your revenue from subscriptions and micropayments.</p>
  </div>;
const DashboardSettings = () => <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
    <h2 className="text-2xl font-bold mb-4">Settings</h2>
    <p>Configure your account and notification preferences.</p>
  </div>;
const DashboardProfile = () => <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
    <h2 className="text-2xl font-bold mb-4">Profile</h2>
    <p>Update your bio, profile picture, and journalist credentials.</p>
  </div>;
export default Dashboard;
